import type { ReactNode } from "react";

/**
 * Frame for a chart: title, one line of explanation, the drawing, and a collapsible table with the same numbers
 * (the accessible twin of the drawing).
 */
export function ChartFrame({
  id,
  title,
  subtitle,
  legend,
  table,
  children,
}: {
  id: string;
  title: string;
  subtitle: string;
  legend?: ReactNode;
  table: { caption: string; head: string[]; rows: (string | number)[][] };
  children: ReactNode;
}) {
  return (
    <figure aria-labelledby={`${id}-title`} className="m-0">
      <figcaption>
        <h3 id={`${id}-title`} className="font-display text-lg font-semibold text-ink">
          {title}
        </h3>
        <p className="text-sm text-ink-2">{subtitle}</p>
      </figcaption>
      <div className="mt-3">{children}</div>
      {legend && <div className="mt-2">{legend}</div>}
      <details className="mt-3 text-sm">
        <summary className="inline-flex min-h-11 cursor-pointer items-center rounded-lg px-1 font-medium text-ink underline decoration-line underline-offset-4 hover:decoration-ink">
          Werte als Tabelle anzeigen
        </summary>
        <div className="mt-2 overflow-x-auto" role="region" aria-label={table.caption} tabIndex={0}>
          <table className="w-full border-collapse text-left tabular-nums">
            <caption className="sr-only">{table.caption}</caption>
            <thead>
              <tr className="border-b border-line">
                {table.head.map((h, i) => (
                  <th key={i} scope="col" className={`whitespace-nowrap px-2 py-1.5 font-semibold text-ink ${i > 0 ? "text-right" : ""}`}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {table.rows.map((row, r) => (
                <tr key={r} className="border-b border-line/60">
                  {row.map((cell, i) =>
                    i === 0 ? (
                      <th key={i} scope="row" className="whitespace-nowrap px-2 py-1.5 font-medium text-ink">
                        {cell}
                      </th>
                    ) : (
                      <td key={i} className="whitespace-nowrap px-2 py-1.5 text-right text-ink">
                        {cell}
                      </td>
                    ),
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </figure>
  );
}

/** Tooltip box that sits next to a point inside a `relative` chart wrapper and flips near the right edge. */
export function ChartTooltip({ x, width, children }: { x: number; width: number; children: ReactNode }) {
  const flip = x > width / 2;
  return (
    <div
      role="presentation"
      className="pointer-events-none absolute top-2 z-10 rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink shadow-md"
      style={{ left: x, transform: flip ? "translateX(calc(-100% - 12px))" : "translateX(12px)" }}
    >
      {children}
    </div>
  );
}
